import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Col,
  Container,
  Row,
  Card,
  CardBody,
  Input,
  Button,
  Modal,
  ModalBody,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";

import BreadCrumb from "Components/Common/BreadCrumb";
import Loader from "Components/Common/Loader";

import type { RootState } from "store";
import type { ThunkDispatch } from "redux-thunk";
import type { AnyAction } from "redux";

import {
  fetchTicketCategories,
  createTicketCategory,
  editTicketCategory,
  deleteTicketCategory,
} from "slices/Ticket/thunk";
import TicketCategoryTable from "./ticketContainer";
import TicketCategoryForm from "./ticketcategoryform";
import NewDeleteModal from "Components/Common/NewDeleteModal";
import { handleValidDate, handleValidTime } from "helpers/common_helper";

const selectTicketState = (state: RootState | any) =>
  state?.Ticket ?? state?.ticket ?? {};

const ticketSelector = createSelector(selectTicketState, (ticket: any = {}) => ({
  categories: ticket.ticketCategories || [],
  loading: ticket.ticketCategoryLoading,
}));

const priorityStyle = (priority: number) => {
  switch (priority) {
    case 3:
      return { label: "High", color: "#dc3545", bg: "#f8d7da" };
    case 2:
      return { label: "Medium", color: "#fd7e14", bg: "#fff3cd" };
    case 1:
    default:
      return { label: "Low", color: "#198754", bg: "#d1e7dd" };
  }
};

const TicketCategoryPage: React.FC = () => {
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();
  const navigate = useNavigate();

  const { categories, loading } = useSelector(ticketSelector);

  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editCategory, setEditCategory] = useState<any | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    null
  );

  const validation = useFormik({
    initialValues: {
      categoryname: "",
      priority: "",
    },
    validationSchema: Yup.object({
      categoryname: Yup.string().required("Category name is required"),
      priority: Yup.number()
        .typeError("Priority is required")
        .required("Priority is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      const payload = {
        categoryname: values.categoryname.trim(),
        priority: Number(values.priority),
      };

      let res: any;

      if (isEdit && editCategory?.categoryid) {
        res = await dispatch(
          editTicketCategory({
            categoryid: Number(editCategory.categoryid),
            ...payload,
          }) as any
        );
      } else {
        res = await dispatch(createTicketCategory(payload as any) as any);
      }

      if (res?.success !== false) {
        resetForm();
        setIsModalOpen(false);
        setIsEdit(false);
        setEditCategory(null);
      }
    },
  });

  const toggleModal = () => {
    if (!isModalOpen) {
      // opening for add
      validation.resetForm();
      setIsEdit(false);
      setEditCategory(null);
    }
    setIsModalOpen(!isModalOpen);
  };

  const loadCategories = useCallback(() => {
    return dispatch(fetchTicketCategories() as any);
  }, [dispatch]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleEditClick = useCallback(
    (row: any) => {
      setIsEdit(true);
      setEditCategory(row);
      setIsModalOpen(true);

      validation.setValues({
        categoryname: row.categoryname ?? "",
        priority: row.priority ?? "",
      });
    },
    [validation]
  );

  const handleDeleteClick = useCallback(
    (categoryid: number) => {
      setSelectedCategoryId(categoryid);
      setDeleteModalOpen(true);
    },
    []
  );

  const filteredCategories = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return categories;

    return categories.filter((c: any) =>
      c.categoryname?.toLowerCase().includes(q)
    );
  }, [categories, searchQuery]);

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "categoryid",
        cell: ({ row }: any) => `#TK${row.original.categoryid}`,
      },
      {
        header: "Category Name",
        accessorKey: "categoryname",
        cell: ({ row }: any) => `${row.original.categoryname}`,
      },
      {
        header: "Priority",
        accessorKey: "priority",
        cell: ({ row }: any) => {
          const { label, color, bg } = priorityStyle(row.original.priority);
          return (
            <span
              style={{
                display: "inline-block",
                minWidth: 70,
                textAlign: "center",
                padding: "6px 12px",
                borderRadius: "16px",
                color,
                background: bg,
                fontWeight: 600,
                fontSize: "12px",
              }}
            >
              {label}
            </span>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: (cell: any) => {
            const value = cell.getValue();
            const lowerVal = value?.toString().toLowerCase();

            const statusColor = lowerVal === "active" ? "#159E3A" : "#A65F00"; 

            return (
            <span
                style={{
                display: "inline-block",
                padding: "4px 12px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: "500",
                color: statusColor,
                border: `1px solid ${statusColor}`,
                textTransform: "capitalize",
                }}
            >
                {value}
            </span>
            );
        },
      },      {
        header: "Created on",
        accessorKey: "createdon",
        cell: ({ row }: any) => {
          return row.original.createdon ? (
            <>
              {handleValidDate(row.original.createdon)},{" "}
              <small className="text-muted">{handleValidTime(row.original.createdon)}</small>
            </>
          ) : (
            <>—</>
          );
        },
      },
      {
        header: "Created by",
        accessorKey: "createdby",
        cell: ({ row }: any) =>
            row.original.status ? `${row.original.createdby.charAt(0).toUpperCase() + row.original.createdby.slice(1)}` : "—",
      },
      {
        header: "Actions",
        accessorKey: "actions",
        enableColumnFilter: false,
        cell: (cellProps: any) => {
          const row = cellProps.row.original;
  
          return (
            <ul className="list-inline hstack gap-1 mb-0 justify-content-center">
              <li className="list-inline-item" title="Edit">
                <Button
                  color=""
                  className="btn btn-icon btn-soft-primary btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditClick(row);
                  }}
                >
                  <i className="ri-pencil-fill"></i>
                </Button>
              </li>
  
              {/* Delete */}
              <li className="list-inline-item" title="Delete">
                <Button
                  color=""
                  className="btn btn-icon btn-soft-danger btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(row.categoryid);
                  }}
                >
                  <i className="ri-delete-bin-line"></i>
                </Button>
              </li>
            </ul>
          );
        },
      },
    ],
    [handleEditClick, handleDeleteClick]
  );

  return (
    <div className="page-content">
      <Container fluid className="border pt-4 rounded-4 bg-light mb-3 ms-3 pe-4">
        <BreadCrumb
          title="Tickets Category"
          pageTitle={["Tickets", "Category"]}
        />

        {/* Header Row (same as DeliveryPage) */}
        <Row className="align-items-center g-3 d-flex justify-content-between pb-4 pe-3">

          <Col md={3}>
            <div className="position-relative">
              <Input
                type="text"
                className="form-control border ps-5 ms-2 rounded-3"
                value={searchQuery}
                placeholder="Search ticket ..."
                disabled
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <i
                className="ri-search-line position-absolute top-50 start-0 translate-middle-y text-muted ms-3"
                style={{ fontSize: "1.1rem" }}
              />
            </div>
          </Col>

          <Col
            md={7}
            className="text-end d-flex justify-content-end gap-2 align-items-center"
          >
            <Button
              color="primary"
              outline
              onClick={() => navigate("/ticket")}
            >
              <i className="ri-stack-line align-middle me-1" />
              View Tickets
            </Button>
            <Button color="primary" onClick={toggleModal}>
              + Add Category
            </Button>
          </Col>
        </Row>

        {/* Table */}
        <Row>
          <Col lg={12}>
            <Card className="border-0 shadow-none">
              <CardBody>
                {!loading ? (
                  filteredCategories.length > 0 ? (
                    <TicketCategoryTable
                      columns={columns}
                      data={filteredCategories}
                      tableClass="align-middle border-bottom rounded-pill"
                      theadClass="bg-primary text-white border-top border-success"
                      divClass="table-responsive table-card"
                    />
                  ) : (
                    <div className="text-center p-4">No records found.</div>
                  )
                ) : (
                  <Loader />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal isOpen={isModalOpen} toggle={toggleModal} centered size="lg">
        <ModalBody>
          <TicketCategoryForm validation={validation} isEdit={isEdit} />
        </ModalBody>
      </Modal>

      <NewDeleteModal
        show={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Category"
        message="Are you sure you want to delete this ticket category?"
        onConfirm={async () => {
          if (selectedCategoryId) {
            setDeleteModalOpen(false);
            const categoryid = selectedCategoryId;
            setSelectedCategoryId(null);
            await dispatch(deleteTicketCategory(categoryid) as any);
          }
        }}
      />
    </div>
  );
};

export default TicketCategoryPage;
